import type { LinkExitError } from '@moneykit/connect';
import type { PlaidLinkOnEventMetadata } from 'react-plaid-link';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MoneykitConnectButton } from 'src/components/moneykit/moneykit-connect-button';
import PlaidLinkButton from 'src/components/plaid/plaid-link-button';

import type { ButtonProps } from '@savvy-kit/components/button';
import type { PlaidProduct } from '@savvy/constants/generated/gql-enums';
import type { ID } from '@savvy/utils/id';

// Vendor-agnostic "Connect" button. Reads the LaunchDarkly flag
// `flag-moneykit-connect-enabled` and renders either the new MoneyKit
// branch or the legacy Plaid branch.
//
// Why one wrapper and not parallel callsites? Several entry points are
// going to add new-PA flows during the COB-03 dogfood phase (add-item,
// onboarding, advisor-side admin pages). Putting the flag check here
// means each consumer stays vendor-agnostic.
//
// The Plaid branch stays as the kill switch until P2.3 (full Plaid
// removal). Once Plaid is decommissioned, this component collapses to
// just the MoneyKit branch (or gets deleted in favor of MoneykitConnectButton).
type AggregatorConnectButtonProps = {
  readonly title: string;
  readonly subtitle?: string;
  readonly onSuccess: Deprecated_HandleItemUpdate;
  readonly setIsLoading: (isLoading: boolean) => void;
  readonly buttonProps?: ButtonProps;
  readonly providerAccountId?: ID;
  readonly providerAccountSource?: string | null;
  readonly clientUserId?: ID;
  // Plaid-only — ignored on the MoneyKit branch since MoneyKit infers
  // products from the underlying link.
  readonly products?: PlaidProduct[];
  readonly accountFilters?: string;
  readonly onPlaidError?: (metadata: PlaidLinkOnEventMetadata) => void;
  readonly onMoneykitError?: (error: LinkExitError) => void;
  readonly requestHeaders?: Readonly<Record<string, string>>;
  // When supplied, a successful MoneyKit exchange will mark the named
  // Plaid ProviderAccount as DEPRECATED — used by the Plaid → MoneyKit
  // migration banner once that flow lands.
  readonly replacingProviderAccountId?: ID;
};

export function AggregatorConnectButton({
  title,
  subtitle,
  onSuccess,
  setIsLoading,
  buttonProps,
  providerAccountId,
  providerAccountSource,
  clientUserId,
  products,
  accountFilters,
  onPlaidError,
  onMoneykitError,
  requestHeaders,
  replacingProviderAccountId,
}: AggregatorConnectButtonProps) {
  const { flagMoneykitConnectEnabled: moneykitEnabled } = useFlags();

  const isPlaidPaMigration = moneykitEnabled && providerAccountSource === 'PLAID' && !!providerAccountId;
  const effectiveReplacingId = replacingProviderAccountId ?? (isPlaidPaMigration ? providerAccountId : undefined);

  if (moneykitEnabled) {
    return (
      <MoneykitConnectButton
        title={title}
        subtitle={subtitle}
        onSuccess={onSuccess}
        onError={onMoneykitError}
        setIsLoading={setIsLoading}
        buttonProps={buttonProps}
        providerAccountId={effectiveReplacingId ? undefined : providerAccountId}
        clientUserId={clientUserId}
        requestHeaders={requestHeaders}
        replacingProviderAccountId={effectiveReplacingId}
      />
    );
  }

  return (
    <PlaidLinkButton
      title={title}
      subtitle={subtitle}
      onSuccess={onSuccess}
      onError={onPlaidError}
      setIsLoading={setIsLoading}
      buttonProps={buttonProps}
      providerAccountId={providerAccountId ?? replacingProviderAccountId}
      clientUserId={clientUserId}
      products={products}
      accountFilters={accountFilters}
    />
  );
}
